// client/src/components/public/ConfirmPartnerPage.js
import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ApiPartnerInvite from '../../api/ApiPartnerInvite'

/**
 * Public page for a doubles partner to confirm or decline a registration invite.
 * Accessed via /confirm-partner?token=<uuid>
 * No authentication required.
 */
function ConfirmPartnerPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  // 'loading' | 'pending' | 'confirmed' | 'declined' | 'expired' | 'already_responded' | 'not_found' | 'error'
  const [status, setStatus] = useState('loading')
  const [invite, setInvite] = useState(null)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false)
  const [declineLoading, setDeclineLoading] = useState(false)
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    if (!token) {
      setStatus('not_found')
      return
    }

    let cancelled = false
    async function fetchInvite() {
      try {
        const data = await ApiPartnerInvite.getPartnerInvite(token)
        if (cancelled) return
        const inviteStatus = data?.status
        if (inviteStatus === 'pending') {
          setInvite(data)
          setStatus('pending')
        } else if (inviteStatus === 'confirmed') {
          setInvite(data)
          setStatus('confirmed')
        } else if (inviteStatus === 'declined') {
          setInvite(data)
          setStatus('declined')
        } else if (inviteStatus === 'expired') {
          setInvite(data)
          setStatus('expired')
        } else if (inviteStatus === 'already_responded') {
          setInvite(data)
          setStatus('already_responded')
        } else {
          setStatus('not_found')
        }
      } catch (err) {
        if (cancelled) return
        const httpStatus = err?.status || err?.response?.status
        if (httpStatus === 404) {
          setStatus('not_found')
        } else {
          setStatus('error')
        }
      }
    }

    fetchInvite()
    return () => {
      cancelled = true
    }
  }, [token, retryCount])

  const handleRetry = () => {
    setStatus('loading')
    setRetryCount((count) => count + 1)
  }

  const handleConfirm = async () => {
    setConfirmLoading(true)
    try {
      await ApiPartnerInvite.confirmPartner(token)
      setStatus('confirmed')
    } catch {
      setStatus('error')
    } finally {
      setConfirmLoading(false)
    }
  }

  const handleDecline = async () => {
    setDeclineLoading(true)
    try {
      await ApiPartnerInvite.declinePartner(token)
      setStatus('declined')
    } catch {
      setStatus('error')
    } finally {
      setDeclineLoading(false)
      setShowDeclineConfirm(false)
    }
  }

  return (
    <div
      className="d-flex justify-content-center align-items-start min-vh-100 bg-light"
      style={{ paddingTop: '3rem', paddingBottom: '3rem' }}
    >
      <div
        className="card shadow-sm w-100"
        style={{ maxWidth: 480, margin: '0 1rem' }}
      >
        <div className="card-body p-4">
          {status === 'loading' && (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
              <p className="mt-3 text-muted">Loading your invite…</p>
            </div>
          )}

          {status === 'pending' && invite && (
            <>
              <h2 className="h4 fw-bold mb-1">{invite.league_name}</h2>
              <p className="text-muted mb-4" style={{ fontSize: '0.95rem' }}>
                <strong>{invite.nominator_name}</strong> has invited you to join
                as their doubles partner.
              </p>
              {invite.expires_at && (
                <p className="small text-muted mb-4">
                  This invite expires{' '}
                  {new Date(invite.expires_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                  .
                </p>
              )}

              {!showDeclineConfirm ? (
                <div className="d-flex flex-column gap-2">
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={handleConfirm}
                    disabled={confirmLoading}
                    data-testid="confirm-button"
                  >
                    {confirmLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Confirming…
                      </>
                    ) : (
                      'Confirm'
                    )}
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-lg"
                    onClick={() => setShowDeclineConfirm(true)}
                    disabled={confirmLoading}
                    data-testid="decline-button"
                  >
                    Decline
                  </button>
                </div>
              ) : (
                <div
                  className="border border-warning rounded p-3"
                  data-testid="decline-confirm-section"
                >
                  <p className="mb-3 small">
                    Are you sure you want to decline?
                  </p>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-danger flex-grow-1"
                      onClick={handleDecline}
                      disabled={declineLoading}
                      data-testid="decline-confirm-yes"
                    >
                      {declineLoading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden="true"
                          />
                          Declining…
                        </>
                      ) : (
                        'Yes, decline'
                      )}
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => setShowDeclineConfirm(false)}
                      disabled={declineLoading}
                      data-testid="decline-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {status === 'confirmed' && (
            <div className="text-center py-2" data-testid="confirmed-state">
              <div className="display-4 mb-3">🎾</div>
              <h2 className="h4 fw-bold">You&apos;re confirmed!</h2>
              <p className="text-muted mt-2">
                {invite
                  ? `${invite.nominator_name} has been notified.`
                  : 'Your partner has been notified.'}
              </p>
              <p className="text-muted">
                You&apos;re all set — enjoy the league!
              </p>
            </div>
          )}

          {status === 'declined' && (
            <div className="text-center py-2" data-testid="declined-state">
              <h2 className="h4 fw-bold">Invite declined.</h2>
              <p className="text-muted mt-2">
                {invite
                  ? `${invite.nominator_name} has been notified that you've declined.`
                  : "Your partner has been notified that you've declined."}
              </p>
            </div>
          )}

          {status === 'expired' && (
            <div className="text-center py-2" data-testid="expired-state">
              <h2 className="h4 fw-bold">This invite has expired.</h2>
              <p className="text-muted mt-2">
                The registration window for this partner invite has passed.
                {invite?.nominator_name &&
                  ` ${invite.nominator_name} has been notified.`}
              </p>
              <button
                className="btn btn-secondary mt-2"
                onClick={() => navigate('/landing')}
                data-testid="expired-return-home"
              >
                Return to Home
              </button>
            </div>
          )}

          {status === 'already_responded' && (
            <div
              className="text-center py-2"
              data-testid="already-responded-state"
            >
              <h2 className="h4 fw-bold">
                This invite has already been responded to.
              </h2>
              <p className="text-muted mt-2">
                {invite?.nominator_name && invite?.league_name
                  ? `The invite from ${invite.nominator_name} for ${invite.league_name} has already been responded to.`
                  : invite?.nominator_name
                    ? `The invite from ${invite.nominator_name} has already been responded to.`
                    : 'If this seems unexpected, please contact the league organizer.'}
              </p>
            </div>
          )}

          {status === 'not_found' && (
            <div className="text-center py-2" data-testid="not-found-state">
              <h2 className="h4 fw-bold">
                Invalid or unrecognized invite link.
              </h2>
              <p className="text-muted mt-2">
                Please check the link in your email and try again.
              </p>
              <button
                className="btn btn-secondary mt-2"
                onClick={() => navigate('/landing')}
                data-testid="not-found-return-home"
              >
                Return to Home
              </button>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-2" data-testid="error-state">
              <h2 className="h4 fw-bold">Something went wrong.</h2>
              <p className="text-muted mt-2">
                Please try refreshing the page. If the problem continues,
                contact support.
              </p>
              <div className="d-flex gap-2 justify-content-center mt-2">
                <button
                  className="btn btn-primary"
                  onClick={handleRetry}
                  data-testid="error-retry"
                >
                  Try Again
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigate('/landing')}
                  data-testid="error-return-home"
                >
                  Return to Home
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ConfirmPartnerPage
